/**
 * Danger Zone Component
 *
 * Simplified to 3 clear, robust actions:
 * 1. Stop All Jobs - Emergency brake for running operations
 * 2. Reset Settings - Restore default configuration
 * 3. Factory Reset - Complete system wipe
 */

import { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Modal, Input, Alert, Spin, Result, Badge } from 'antd';
import {
  StopOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ChromeOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

interface StopAllJobsResult {
  workerSignaled: boolean;
  queuePaused: boolean;
  bullmqJobsRemoved: number;
  cellLocksCleared: number;
  dbJobsCancelled: number;
  workerRestarted: boolean;
}

interface FactoryResetResult {
  workerShutdown: boolean;
  redisKeysDeleted: number;
  jobsDeleted: number;
  placesDeleted: number;
  cellsDeleted: number;
  reviewsDeleted: number;
  proxyStatsDeleted: number;
  settingsReset: boolean;
  workerRestarted: boolean;
}

interface KillChromeResult {
  processesFound: number;
  processesKilled: number;
}

interface DangerZoneProps {
  onStopAllJobs: () => Promise<{ message: string; details: StopAllJobsResult }>;
  onResetSettings: () => Promise<{ message: string }>;
  onFactoryReset: () => Promise<{ message: string; details: FactoryResetResult }>;
  onKillChrome: () => Promise<{ message: string; details: KillChromeResult }>;
  onGetChromeCount: () => Promise<{ count: number; scraperId: string }>;
}

type ActionType = 'stopAllJobs' | 'resetSettings' | 'factoryReset' | 'killChrome';

interface ActionConfig {
  title: string;
  description: string;
  confirmText: string;
  icon: React.ReactNode;
  buttonText: string;
  isDangerous: boolean;
  warningMessage: string;
}

const ACTION_CONFIGS: Record<ActionType, ActionConfig> = {
  stopAllJobs: {
    title: 'Stop All Jobs',
    description: 'Immediately stop all running and pending jobs. This will close all browser instances, clear the job queue, and cancel all active operations.',
    confirmText: 'STOP',
    icon: <StopOutlined />,
    buttonText: 'Stop All Jobs',
    isDangerous: false,
    warningMessage: 'All running scrape operations will be terminated. Any progress on incomplete jobs will be lost.',
  },
  resetSettings: {
    title: 'Reset Settings',
    description: 'Restore all scraper settings to their default values. This includes concurrency, timeouts, and browser configuration.',
    confirmText: 'RESET',
    icon: <SettingOutlined />,
    buttonText: 'Reset Settings',
    isDangerous: false,
    warningMessage: 'Your custom settings will be lost and replaced with defaults.',
  },
  factoryReset: {
    title: 'Factory Reset',
    description: 'Completely wipe all data and start fresh. This will delete ALL jobs, results, and cached data from both the database and Redis.',
    confirmText: 'FACTORY RESET',
    icon: <ThunderboltOutlined />,
    buttonText: 'Factory Reset',
    isDangerous: true,
    warningMessage: 'This will PERMANENTLY DELETE all your scraped data, job history, and cached information. This action cannot be undone!',
  },
  killChrome: {
    title: 'Kill Chrome Processes',
    description: 'Kill all orphaned Chrome processes from this scraper. Only affects OUR processes - safe on shared servers.',
    confirmText: 'KILL',
    icon: <ChromeOutlined />,
    buttonText: 'Kill Chrome',
    isDangerous: false,
    warningMessage: 'This will kill all Chrome processes belonging to this scraper. Only use when you know no jobs are running!',
  },
};

export function DangerZone({
  onStopAllJobs,
  onResetSettings,
  onFactoryReset,
  onKillChrome,
  onGetChromeCount,
}: DangerZoneProps) {
  const [activeAction, setActiveAction] = useState<ActionType | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; details?: unknown } | null>(null);
  const [chromeCount, setChromeCount] = useState<number>(0);

  // Fetch Chrome count periodically
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await onGetChromeCount();
        setChromeCount(data.count);
      } catch {
        // Ignore errors
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [onGetChromeCount]);

  const handleOpenModal = (action: ActionType) => {
    setActiveAction(action);
    setConfirmInput('');
    setResult(null);
  };

  const handleCloseModal = () => {
    setActiveAction(null);
    setConfirmInput('');
    setResult(null);
  };

  const handleExecute = async () => {
    if (!activeAction) return;

    const config = ACTION_CONFIGS[activeAction];
    if (confirmInput !== config.confirmText) return;

    setIsExecuting(true);
    setResult(null);

    try {
      let response: { message: string; details?: unknown };

      switch (activeAction) {
        case 'stopAllJobs':
          response = await onStopAllJobs();
          break;
        case 'resetSettings':
          response = await onResetSettings();
          break;
        case 'factoryReset':
          response = await onFactoryReset();
          break;
        case 'killChrome':
          response = await onKillChrome();
          // Refresh the count after killing
          try {
            const data = await onGetChromeCount();
            setChromeCount(data.count);
          } catch {
            // Ignore
          }
          break;
      }

      setResult({
        success: true,
        message: response.message,
        details: response.details,
      });
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : 'Operation failed',
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const activeConfig = activeAction ? ACTION_CONFIGS[activeAction] : null;

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: '#ff4d4f' }} />
          <span style={{ color: '#ff4d4f' }}>Danger Zone</span>
        </Space>
      }
      style={{ borderColor: '#ff4d4f' }}
    >
      <Alert
        type="warning"
        message="These actions affect your running operations and data. Use with caution."
        style={{ marginBottom: 24 }}
        showIcon
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Stop All Jobs */}
        <ActionRow
          config={ACTION_CONFIGS.stopAllJobs}
          onClick={() => handleOpenModal('stopAllJobs')}
        />

        {/* Reset Settings */}
        <ActionRow
          config={ACTION_CONFIGS.resetSettings}
          onClick={() => handleOpenModal('resetSettings')}
        />

        {/* Factory Reset */}
        <ActionRow
          config={ACTION_CONFIGS.factoryReset}
          onClick={() => handleOpenModal('factoryReset')}
        />

        {/* Kill Chrome - with badge showing count */}
        <ActionRowWithBadge
          config={ACTION_CONFIGS.killChrome}
          onClick={() => handleOpenModal('killChrome')}
          badgeCount={chromeCount}
        />
      </Space>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: activeConfig?.isDangerous ? '#ff4d4f' : '#faad14' }} />
            {activeConfig?.title}
          </Space>
        }
        open={!!activeAction}
        onCancel={handleCloseModal}
        footer={
          result ? (
            <Button onClick={handleCloseModal}>Close</Button>
          ) : (
            [
              <Button key="cancel" onClick={handleCloseModal} disabled={isExecuting}>
                Cancel
              </Button>,
              <Button
                key="confirm"
                type="primary"
                danger={activeConfig?.isDangerous}
                loading={isExecuting}
                disabled={confirmInput !== activeConfig?.confirmText}
                onClick={handleExecute}
              >
                {isExecuting ? 'Executing...' : 'Confirm'}
              </Button>,
            ]
          )
        }
        width={500}
      >
        {isExecuting ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 16 }}>
              Executing {activeConfig?.title.toLowerCase()}...
            </Paragraph>
            <Paragraph type="secondary">
              This may take a few seconds. Please wait.
            </Paragraph>
          </div>
        ) : result ? (
          <Result
            status={result.success ? 'success' : 'error'}
            title={result.success ? 'Operation Complete' : 'Operation Failed'}
            subTitle={result.message}
            extra={
              result.details && (
                <div style={{
                  textAlign: 'left',
                  background: 'var(--ant-color-bg-container)',
                  border: '1px solid var(--ant-color-border)',
                  padding: 16,
                  borderRadius: 8
                }}>
                  <Text strong>Details:</Text>
                  <pre style={{
                    margin: '8px 0 0',
                    fontSize: 12,
                    overflow: 'auto',
                    color: 'var(--ant-color-text)',
                    background: 'var(--ant-color-bg-elevated)',
                    padding: 12,
                    borderRadius: 4,
                  }}>
                    {JSON.stringify(result.details, null, 2)}
                  </pre>
                </div>
              )
            }
          />
        ) : (
          <>
            <Alert
              type={activeConfig?.isDangerous ? 'error' : 'warning'}
              message={activeConfig?.warningMessage}
              style={{ marginBottom: 16 }}
              showIcon
            />

            <Paragraph>{activeConfig?.description}</Paragraph>

            <Paragraph type="secondary" style={{ marginTop: 16 }}>
              Type <Text code strong>{activeConfig?.confirmText}</Text> to confirm:
            </Paragraph>

            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value.toUpperCase())}
              placeholder={activeConfig?.confirmText}
              size="large"
              status={confirmInput && confirmInput !== activeConfig?.confirmText ? 'error' : undefined}
            />
          </>
        )}
      </Modal>
    </Card>
  );
}

/**
 * Single action row component
 */
function ActionRow({
  config,
  onClick,
}: {
  config: ActionConfig;
  onClick: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        background: config.isDangerous
          ? 'rgba(255, 77, 79, 0.06)'
          : 'var(--ant-color-bg-elevated)',
        borderRadius: 8,
        border: config.isDangerous
          ? '1px solid rgba(255, 77, 79, 0.3)'
          : '1px solid var(--ant-color-border)',
      }}
    >
      <div style={{ flex: 1 }}>
        <Space>
          <span style={{
            fontSize: 18,
            color: config.isDangerous ? '#ff4d4f' : 'var(--ant-color-text-secondary)'
          }}>
            {config.icon}
          </span>
          <div>
            <Text strong style={{ display: 'block' }}>
              {config.title}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {config.description.split('.')[0]}.
            </Text>
          </div>
        </Space>
      </div>
      <Button
        danger={config.isDangerous}
        icon={config.icon}
        onClick={onClick}
      >
        {config.buttonText}
      </Button>
    </div>
  );
}

/**
 * Action row with badge (for Chrome count)
 */
function ActionRowWithBadge({
  config,
  onClick,
  badgeCount,
}: {
  config: ActionConfig;
  onClick: () => void;
  badgeCount: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        background: config.isDangerous
          ? 'rgba(255, 77, 79, 0.06)'
          : 'var(--ant-color-bg-elevated)',
        borderRadius: 8,
        border: config.isDangerous
          ? '1px solid rgba(255, 77, 79, 0.3)'
          : '1px solid var(--ant-color-border)',
      }}
    >
      <div style={{ flex: 1 }}>
        <Space>
          <span style={{
            fontSize: 18,
            color: config.isDangerous ? '#ff4d4f' : 'var(--ant-color-text-secondary)'
          }}>
            {config.icon}
          </span>
          <div>
            <Space>
              <Text strong>
                {config.title}
              </Text>
              <Badge
                count={badgeCount}
                showZero
                style={{
                  backgroundColor: badgeCount > 0 ? '#faad14' : '#52c41a',
                }}
              />
            </Space>
            <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
              {config.description.split('.')[0]}.
            </Text>
          </div>
        </Space>
      </div>
      <Button
        danger={config.isDangerous}
        icon={config.icon}
        onClick={onClick}
        disabled={badgeCount === 0}
      >
        {badgeCount === 0 ? 'No Processes' : config.buttonText}
      </Button>
    </div>
  );
}

export default DangerZone;
